import { useToast } from '@/hooks/use-toast';
import { useChatNavigation } from '@/utils/navigationUtils';
import { ChatMessageToast, type ChatMessageNotification } from '@/components/ui/ChatMessageToast';

/**
 * Enhanced toast hook that provides custom toast functionality
 * including chat message toasts with navigation
 */
export const useCustomToast = () => {
  const { toast: baseToast, ...baseToastMethods } = useToast();
  const { navigateToSession } = useChatNavigation();

  /**
   * Show a custom chat message toast
   */
  const showChatMessageToast = (notification: ChatMessageNotification) => {
    const handleToastClick = () => {
      // close the toast
      baseToastMethods.dismiss();
      
      // Navigate to the messages page with the specific session
      navigateToSession(notification.metadata.sessionId);
    };

    // Create the custom toast with ChatMessageToast component
    const toastId = baseToast({
      // Override default styling for custom toast
      className: 'group border-none shadow-lg p-0 bg-transparent w-full max-w-md',
      duration: 8000, // Show for 8 seconds

      // Custom description that renders our ChatMessageToast component
      description: (
        <ChatMessageToast
          notification={notification}
          onClick={handleToastClick}
          className="w-full"
        />
      ),

      // Optional action button as fallback
      action: (
        <>
        </>
        // <ToastAction
        //   altText="Open Chat"
        //   onClick={handleToastClick}
        //   className="ml-2 bg-blue-600 text-white hover:bg-blue-700"
        // >
        //   Reply
        // </ToastAction>
      ),
    });

    return toastId;
  };

  /**
   * Show a regular toast (wrapper around base toast)
   */
  const showToast = baseToast;

  /**
   * Show success toast
   */
  const showSuccessToast = (title: string, description?: string) => {
    return baseToast({
      title,
      description,
      variant: 'default',
      className: 'border-green-200 bg-green-50 text-green-900',
    });
  };

  /**
   * Show error toast
   */
  const showErrorToast = (title: string, description?: string) => {
    return baseToast({
      title,
      description,
      variant: 'destructive',
    });
  };

  /**
   * Show warning toast
   */
  const showWarningToast = (title: string, description?: string) => {
    return baseToast({
      title,
      description,
      className: 'border-yellow-200 bg-yellow-50 text-yellow-900',
    });
  };

  /**
   * Show info toast
   */
  const showInfoToast = (title: string, description?: string) => {
    return baseToast({
      title,
      description,
      className: 'border-blue-200 bg-blue-50 text-blue-900',
    });
  };

  return {
    // Custom toast methods
    showChatMessageToast,
    showSuccessToast,
    showErrorToast,
    showWarningToast,
    showInfoToast,

    // Base toast method
    showToast,

    // All base toast methods (dismiss, etc.)
    ...baseToastMethods,
  };
};